/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];

    const NAME = feature.getSettingValue('NAME');
    const PORT = feature.getSettingValue('PORT');
    const PIN = feature.getSettingValue('ADC');
    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);
    let pinconfigCode = '';
    let channelDefinition = '';
    let channelNum = 0;
    let adcIndex = 0;

    if (!PIN) {
        return blocks;
    }

    for (let i = 0; i < feature.project.features.length; i++) {
        const tmpFeature = feature.project.features[i];
        if (tmpFeature.id === 'adc' && tmpFeature.getSettingValue('PORT') === PORT) {
            if (tmpFeature.getID() === feature.getID()) {
                break;
            }
            adcIndex++;
        }
    }

    blocks.push({
        location: 'PRJ_CONF',
        id: 'CONFIG_ADC=y',
        blockType: BlockType.CODE,
        template: `
CONFIG_ADC=y 
CONFIG_ADC_ASYNC=y`,
    });

    if (device.soc.indexOf('mim') >= 0) {
        const pinConstant = device.pins.find((pin: any) => pin.num === PIN).constant;
        const parts = pinConstant.split('_');
        parts.pop();
        parts.pop();
        const pinPrefix = parts.join('_');
        channelNum = (parseInt(parts.pop(), 10) + 5) % 16;


        pinconfigCode = `
        pinmux = <&${pinPrefix}_${PORT}_in${channelNum}>;
        bias-disable;
        drive-strength = "r0-6";
        slew-rate = "slow";
        nxp,speed = "100-mhz";
`;

        channelDefinition = `
channel@${channelNum} {
    reg = <${channelNum}>;
    zephyr,gain = "ADC_GAIN_1";
    zephyr,reference = "ADC_REF_INTERNAL";
    zephyr,acquisition-time = <ADC_ACQ_TIME_DEFAULT>;
    zephyr,resolution = <12>; 
};
`;
    }

    if (pinconfigCode !== '') {
        blocks.push({
            location: 'APP_OVERLAY_PINCTRL',
            id: `${PORT}_default`,
            blockType: BlockType.CODE,
            template: `
${PORT}_default: ${PORT}_default {
    /****${PORT}_default****/
};`,
        });

        blocks.push({
            location: `${PORT}_default`,
            blockType: BlockType.CODE,
            template: `
group${adcIndex} {
    ${pinconfigCode}
};
            `,
        });

        blocks.push({
            location: 'APP_OVERLAY',
            id: `APP_OVERLAY_ADC_CHANNEL_DEFINITION_${PORT}`,
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    #address-cells = <1>;
    #size-cells = <0>;
    status = "okay";
    pinctrl-names = "default";
    
    pinctrl-0 = <&${PORT}_default>;
    
    /****APP_OVERLAY_ADC_CHANNEL_DEFINITION_${PORT}****/
};`,
        });

        blocks.push({
            location: `APP_OVERLAY_ADC_CHANNEL_DEFINITION_${PORT}`,
            blockType: BlockType.CODE,
            template: channelDefinition,
        });

        blocks.push({
            location: 'APP_OVERLAY_DTS',
            id: 'APP_OVERLAY_DTS_ADC_PORTS',
            blockType: BlockType.LOCATION,
            template: `
adc_ports: adc_ports {
    compatible = "adc,adc-devices";
    status = "okay";
    /****APP_OVERLAY_DTS_ADC_PORTS****/
};
`,
        });

        blocks.push({
            location: 'APP_OVERLAY_DTS_ADC_PORTS',
            blockType: BlockType.CODE,
            template: `
${PORT}_${channelNum}: ${PORT}_${channelNum} {
    io-channels = <&${PORT} ${channelNum}>;
};
`,
        });

        blocks.push({
            location: 'test.yml',
            blockType: BlockType.CODE,
            template: `

# SPDX-License-Identifier: Apache-2.0
description: >
  This node can have multiple child sub-nodes. Each child sub-node references
  an ADC channel via 'io-channels'.

compatible: "my-company,adc-sensor"

# Now define how child nodes look:
child-binding:
  description: Child nodes define ADC channel usage
  properties:
    io-channels:
      type: phandle-array
      required: true
      description: >
        References the hardware ADC channel. Typically <&adc1 X>.
`,
        });
    }


    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
int16_t buf_${NAME};

static const struct adc_dt_spec ${PORT}_${channelNum} = ADC_DT_SPEC_GET_BY_IDX(DT_NODELABEL(${PORT}_${channelNum}), 0);
`,
    });

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: 'read_adc_declaration',
        blockType: BlockType.CODE,
        template: `
void read_adc(struct adc_dt_spec *adc, int16_t *buf);
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: 'read_adc_definition',
        blockType: BlockType.CODE,
        template: `
void read_adc(const struct adc_dt_spec *adc, int16_t *buf) {
    int err;

    struct adc_sequence sequence = {
    .buffer = buf,
    .buffer_size = sizeof(*buf),
    };
    
    if (!adc_is_ready_dt(adc)) {
        LOG_INF("ADC controller device %s not ready\\n", adc->dev->name);
    }
    err = adc_channel_setup_dt(adc);
    if (err < 0) {
        LOG_INF("Could not setup channel (%d)\\n", err);
    }

    (void)adc_sequence_init_dt(adc, &sequence);

    err = adc_read_dt(adc, &sequence);
    if (err < 0) {
        LOG_INF("error %d", err);
    }
}
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        id: '#include <zephyr/drivers/adc.h>',
        template: `
#include <zephyr/drivers/adc.h>      
`,
    });


    blocks.push({
        location: 'MAIN',
        blockType: BlockType.CODE,
        template: `
read_adc(&${PORT}_${channelNum}, &buf_${NAME});
`,
    });

    blocks.push({
        location: `var_${NAME}_update`,
        blockType: BlockType.CODE,
        template: `
var_${NAME}_set((int) buf_${NAME});
`,
    });

    return blocks;
};
